<template>
  <div
    class="modal-overlay fixed inset-0 z-50 flex items-center justify-center bg-slate-900/55 p-4 backdrop-blur-[3px] sm:p-6"
    @click.self="close"
  >
    <div
      class="modal-content relative w-full max-w-lg overflow-hidden rounded-2xl border border-gray-200/90 bg-white shadow-2xl shadow-slate-900/10 ring-1 ring-black/[0.04] animate-scale-in dark:border-gray-700/90 dark:bg-gray-900 dark:shadow-black/40 dark:ring-white/[0.06]"
      @click.stop
    >
      <div
        class="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500"
        aria-hidden="true"
      />

      <header
        class="modal-header flex items-start gap-4 border-b border-gray-100 px-6 pb-4 pt-5 dark:border-gray-800"
      >
        <div
          class="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500/15 to-orange-600/20 text-amber-600 dark:from-amber-400/20 dark:to-orange-500/15 dark:text-amber-400"
        >
          <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
        </div>
        <div class="min-w-0 flex-1 pt-0.5">
          <h2 class="modal-title text-lg font-semibold tracking-tight text-gray-900 dark:text-white">
            {{ t('kanban.editColumn.title') }}
          </h2>
          <p class="mt-1 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
            {{ t('kanban.editColumn.subtitle') }}
          </p>
        </div>
        <button
          type="button"
          class="modal-close shrink-0 rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200"
          :aria-label="t('common.close')"
          @click="close"
        >
          <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </header>

      <form @submit.prevent="updateColumn" class="modal-form space-y-5 px-6 py-5">
        <div class="form-group space-y-2">
          <label class="form-label text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {{ t('kanban.editColumn.nameLabel') }} <span class="text-rose-500">*</span>
          </label>
          <input
            v-model="form.nome"
            type="text"
            class="form-input"
            :placeholder="t('kanban.editColumn.namePlaceholder')"
            required
          />
        </div>

        <div class="form-group space-y-3">
          <label class="form-label text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {{ t('kanban.editColumn.colorLabel') }}
          </label>
          <div class="color-picker space-y-3">
            <div class="color-options flex flex-wrap gap-2.5">
              <button
                v-for="color in colorOptions"
                :key="color.value"
                type="button"
                :class="[
                  'color-option h-9 w-9 shrink-0 rounded-full border-2 border-white shadow-md ring-2 ring-transparent transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 dark:border-gray-900 dark:ring-offset-gray-900',
                  form.cor === color.value
                    ? 'ring-indigo-500 ring-offset-2 ring-offset-gray-50 dark:ring-offset-gray-900 scale-105'
                    : 'ring-gray-200/80 dark:ring-gray-700'
                ]"
                :style="{ backgroundColor: color.value }"
                :title="color.name"
                :aria-pressed="form.cor === color.value"
                @click="form.cor = color.value"
              />
            </div>
            <div class="flex items-center gap-3 rounded-xl border border-gray-200/90 bg-gray-50/80 px-3 py-2.5 dark:border-gray-700 dark:bg-gray-800/50">
              <span class="text-xs font-medium text-gray-500 dark:text-gray-400">{{ t('kanban.editColumn.customize') }}</span>
              <input v-model="form.cor" type="color" class="color-input ml-auto h-10 w-14 cursor-pointer overflow-hidden rounded-lg border border-gray-200 bg-white p-0 dark:border-gray-600 dark:bg-gray-800" />
            </div>
          </div>
        </div>

        <div class="form-group space-y-2">
          <label class="form-label text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {{ t('kanban.editColumn.preview') }}
          </label>
          <div
            class="column-preview overflow-hidden rounded-xl p-4 shadow-inner ring-1 ring-black/10 dark:ring-white/10"
            :style="{ background: previewGradient }"
          >
            <div class="preview-header mb-3 flex items-center justify-between gap-2">
              <h3 class="preview-title truncate text-sm font-semibold text-white drop-shadow-sm">
                {{ form.nome || t('kanban.editColumn.columnNameFallback') }}
              </h3>
              <span
                class="preview-count shrink-0 rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-medium text-white backdrop-blur-sm"
              >
                {{ column?.cardsCount || 0 }}
              </span>
            </div>
            <div class="preview-cards space-y-2">
              <div
                class="preview-card rounded-lg border border-white/20 bg-white/15 px-3 py-2 text-sm text-white/95 backdrop-blur-[2px]"
              >
                {{ t('kanban.editColumn.sampleCard') }}
              </div>
            </div>
          </div>
        </div>

        <div
          class="modal-actions flex flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50/60 px-0 pb-0 pt-5 dark:border-gray-800 dark:bg-gray-950/35 sm:flex-row sm:justify-end sm:gap-3 sm:bg-transparent sm:px-0 sm:pt-4"
        >
          <button
            type="button"
            class="btn-secondary inline-flex items-center justify-center rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700/90"
            @click="close"
          >
            {{ t('common.cancel') }}
          </button>
          <button
            type="submit"
            class="btn-primary inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-600 to-orange-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-amber-600/25 transition-all hover:from-amber-500 hover:to-orange-500 hover:shadow-amber-500/30 disabled:pointer-events-none disabled:opacity-50"
            :disabled="loading"
          >
            <span v-if="loading" class="loading-spinner" />
            {{ loading ? t('kanban.editColumn.saving') : t('kanban.editColumn.saveButton') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, reactive, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '@/services/api'
import Swal from 'sweetalert2'

export default {
  name: 'EditColumnModal',
  props: {
    column: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'updated'],
  setup(props, { emit }) {
    const { t } = useI18n()
    const loading = ref(false)

    const form = reactive({
      nome: '',
      cor: '#4f46e5'
    })

    const previewGradient = computed(() => {
      const c = form.cor || '#4f46e5'
      return `linear-gradient(135deg, ${c} 0%, color-mix(in srgb, ${c} 72%, #0f172a) 100%)`
    })

    const colorOptions = computed(() => [
      { name: t('kanban.createColumn.colors.blue'), value: '#4f46e5' },
      { name: t('kanban.createColumn.colors.green'), value: '#10b981' },
      { name: t('kanban.createColumn.colors.yellow'), value: '#f59e0b' },
      { name: t('kanban.createColumn.colors.red'), value: '#ef4444' },
      { name: t('kanban.createColumn.colors.purple'), value: '#8b5cf6' },
      { name: t('kanban.createColumn.colors.pink'), value: '#ec4899' },
      { name: t('kanban.createColumn.colors.gray'), value: '#6b7280' },
      { name: t('kanban.createColumn.colors.orange'), value: '#f97316' },
      { name: t('kanban.createColumn.colors.teal'), value: '#14b8a6' },
      { name: t('kanban.createColumn.colors.indigo'), value: '#6366f1' }
    ])

    const initializeForm = () => {
      if (props.column) {
        form.nome = props.column.nome || ''
        form.cor = props.column.cor || '#4f46e5'
      }
    }

    watch(() => props.column, initializeForm, { immediate: true })

    const close = () => {
      emit('close')
    }

    const updateColumn = async () => {
      try {
        loading.value = true

        const columnData = {
          nome: form.nome,
          cor: form.cor,
          workspaceId: props.column.workspaceId
        }

        console.log('Atualizando coluna:', props.column.id, columnData)

        const response = await api.put(`/colunas-kanban/${props.column.id}`, columnData)

        console.log('Coluna atualizada com sucesso:', response.data)

        emit('updated', response.data)
        emit('close')

        await Swal.fire({
          icon: 'success',
          title: t('kanban.editColumn.successTitle'),
          text: t('kanban.editColumn.successText', { name: response.data.nome }),
          timer: 2000,
          showConfirmButton: false
        })
      } catch (error) {
        console.error('Erro ao atualizar coluna:', error)
        await Swal.fire({
          icon: 'error',
          title: t('kanban.editColumn.errorTitle'),
          text: t('kanban.editColumn.errorGeneric'),
          confirmButtonText: 'OK'
        })
      } finally {
        loading.value = false
      }
    }

    return {
      t,
      loading,
      form,
      colorOptions,
      previewGradient,
      close,
      updateColumn
    }
  }
}
</script>

<style scoped>
.form-input {
  width: 100%;
  padding: 0.625rem 0.875rem;
  background-color: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 0.75rem;
  color: var(--text-primary);
  font-size: 0.875rem;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    background-color 0.15s ease;
}

.form-input::placeholder {
  color: var(--text-muted);
}

.form-input:hover {
  border-color: var(--border-hover);
  background-color: var(--bg-hover);
}

.form-input:focus {
  outline: none;
  border-color: var(--info);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--info) 22%, transparent);
}

.loading-spinner {
  @apply h-4 w-4 shrink-0 rounded-full border-2 border-white border-t-transparent animate-spin;
}
</style>
